// apps/share-web/src/chart/chart-render.ts
//
// Отрисовка чарта в вебе. ВСЯ математика — из общего ядра @bandway/chordpro:
// разбор документа, коллизия «строка-раздел против строки-аккорда», разворот
// {chorus} в копию, склейка аккорда со слогом, транспозиция. Здесь только DOM.
// Ни строки разбора не продублировано: веб читает документ ровно так же, как
// десктоп, потому что читает его тем же кодом.
//
// Про колонки (замок 4, замерено на прототипе). Класс колонок вешается на
// ВНУТРЕННИЙ лист с авто-высотой, а не на прокручиваемый хост. Если повесить на
// хост, у которого высота задана флексом, multicol растекается ПО ГОРИЗОНТАЛИ:
// замер дал scrollWidth 5109 при видимых 966 — играл Vamp, а на экране стояли
// Intro и Verse 1. `column-fill: auto` этого не лечит (колонки набивались на
// четверть высоты).
//
// Табулатура и сетка ({sot}/{sog}) печатаются ДОСЛОВНО — ядро держит их
// содержимое слово в слово ровно для этого, и десктоп поступает так же
// (chart-read.js). Разбирать их нельзя ни в какой степени: аппликатура держится
// на пробелах, а скобки сетки — это не аккорды песни.
//
// Модуль ничего не знает про плеер: ни про плейхед, ни про активный блок.
// Адрес для следования — `data-i` на `.cw-blk`, и это весь контракт наружу.

import { parseChartDoc, renderList, lineToWords, parseChord, renderChord } from '@bandway/chordpro';
import { installLyricsCopy } from './lyrics-text.js';

// Нотация — свойство ЧТЕНИЯ аккорда (H = си, B = си-бемоль), и на десктопе это
// настройка. В шаре её нет: payload несёт текст чарта, а не настройки того, кто
// его опубликовал. Пока переключателя нет, читаем по-английски — так же, как
// ядро по умолчанию.
const NOTATION = 'B' as const;

/** Единица «аккорд + слог» из ядра. */
interface Unit {
  ch: string | null;
  tx: string;
  notext?: boolean;
}

/** Строка блока из ядра: либо текст, либо комментарий-директива. */
interface ChartLine {
  txt?: string | null;
  cmt?: string | null;
  /** Содержимое {sot}/{sog}: печатать ДОСЛОВНО, не разбирая. */
  tab?: boolean;
}

/** Блок документа после разворота {chorus}. */
interface ChartBlock {
  name?: string | null;
  ref?: boolean;
  repeat?: boolean;
  /** Блок целиком дословный — табулатура или сетка. */
  tab?: boolean;
  lines?: ChartLine[];
}

export interface RenderOpts {
  text: string;
  /** Полутона, −7…+7. Слова не двигаются никогда. */
  shift: number;
  /** 1…3. Больше одной — класс на внутреннем листе. */
  columns: number;
}

const el = (tag: string, cls?: string, txt?: string): HTMLElement => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (txt != null) e.textContent = txt;
  return e;
};

// Слово целиком из пробелов — это НЕ слово, это место, где разрешён перенос.
// Единицей «аккорд + слог» оно не является: у него нет ни аккорда, ни слога.
const isGap = (word: Unit[]): boolean =>
  word.length === 1 && word[0].ch === null && /^\s+$/.test(word[0].tx);

function chordText(token: string, shift: number): string {
  const parsed = parseChord(token, NOTATION);
  // Не разобралось — печатаем как написано. Пометка автора вроде «N.C.» не
  // аккорд, но и терять её нельзя.
  return parsed ? renderChord(parsed, shift, NOTATION) : token;
}

// Сдвиг едет ПАРАМЕТРОМ, а не модульной ячейкой. Ячейка работала бы здесь и
// сейчас (renderChart синхронный), но это ловушка на будущее: любой второй
// вызывающий renderLine — частичная перерисовка, инкрементальное обновление из
// следования — молча получил бы чужой сдвиг, и на экране оказалась бы не та
// тональность, без единого падающего теста. Цена честного пути — один аргумент.
function renderLine(text: string, shift: number): HTMLElement {
  const line = el('div', 'cw-ln');
  for (const word of lineToWords(text) as Unit[][]) {
    if (isGap(word)) {
      // Пробел между словами — отдельный элемент, а не единица с пустым
      // аккордом: иначе в чарте заводятся аккорды-призраки, которых в тексте
      // нет, и `.cw-ch` перестаёт значить «здесь аккорд».
      line.appendChild(el('span', 'cw-sp', word[0].tx));
      continue;
    }
    // Слово — единица переноса: слог не должен уехать без своего аккорда.
    const wd = el('span', 'cw-wd');
    for (const u of word) {
      // Два РАЗНЫХ признака, и десктоп тоже держит их двумя классами:
      //  · `is-bare` — аккорд без слога (у ядра `notext`): колонку держим, но
      //    отбиваем справа, иначе соседние аккорды слипаются;
      //  · `is-nochord` — слога аккорд не имеет: колонку аккорда СКЛАДЫВАЕМ,
      //    иначе строка чистых слов несёт над собой пустой ряд высотой в
      //    аккорд, которого на десктопе нет.
      // Имена разошлись с десктопом исторически: там `is-bare` значит ровно
      // «нет аккорда». Переименовать здесь — значит поменять смысл живого
      // класса, поэтому расхождение зафиксировано, а не замаскировано.
      const unit = el('span', 'cw-u' + (u.notext ? ' is-bare' : '') + (u.ch ? '' : ' is-nochord'));
      const ch = el('span', 'cw-ch');
      if (u.ch) ch.textContent = chordText(u.ch, shift);
      unit.appendChild(ch);
      // Аккорд без слога всё равно держит колонку: пустая колонка складывается,
      // и нижнее выравнивание роняет аккорд на уровень слов соседей — ровно
      // ошибка «аккорды не над строками» с живого прогона десктопа.
      unit.appendChild(el('span', 'cw-tx', u.tx || (u.notext ? ' ' : '')));
      wd.appendChild(unit);
    }
    line.appendChild(wd);
  }
  return line;
}

export function renderChart(host: HTMLElement, opts: RenderOpts): void {
  // Дробный сдвиг — не музыка: он дал бы нецелый номер полутона и аккорд
  // напечатался бы как undefined.
  const shift = Number.isFinite(opts.shift) ? Math.round(opts.shift) : 0;
  // Копирование выделения на листе отдаёт слова строками, а не слово на строку
  // (B-261002, chart/lyrics-text.ts). Один слушатель на документ.
  installLyricsCopy(host.ownerDocument);
  host.textContent = '';

  const cols = Math.max(1, Math.min(3, Math.round(opts.columns) || 1));
  const blocks = renderList(parseChartDoc(opts.text ?? '', NOTATION).blocks) as ChartBlock[];
  // Ни листа, ни рамки на пустом тексте: пустой лист — это призрак, который
  // занимает место и ловит прокрутку.
  if (!blocks.length) return;

  const sheet = el('div', 'cw-sheet' + (cols > 1 ? ` cw-c${cols}` : ''));
  host.appendChild(sheet);

  blocks.forEach((b, i) => {
    // `is-verbatim` — на БЛОКЕ, потому что прокрутка вбок нужна ему целиком:
    // строки аппликатуры это шесть струн, и если каждая едет сама, аккорд по
    // вертикали рассыпается. Флаг приходит из ядра (`tab` у блока), а не
    // угадывается по строкам.
    const box = el(
      'div',
      'cw-blk' + (b.repeat ? ' is-repeat' : '') + (b.tab ? ' is-verbatim' : ''),
    );
    box.dataset.i = String(i);

    if (b.ref) {
      // Ссылка выжила разворот — значит припева в документе нет вовсе. Тогда
      // честнее показать указатель, чем заголовок над пустотой, который
      // читается как потерянный текст. Имя печатается ОДИН раз: заголовок и
      // указатель с тем же словом — это просто «Chorus» дважды подряд.
      box.appendChild(el('div', 'cw-ref', b.name ?? ''));
      sheet.appendChild(box);
      return;
    }

    if (b.name) box.appendChild(el('div', 'cw-head', b.name));
    for (const l of b.lines ?? []) {
      if (l.cmt != null) { box.appendChild(el('div', 'cw-cmt', l.cmt)); continue; }
      if (l.txt == null) continue;
      // Содержимое {sot}/{sog} ядро держит ДОСЛОВНО именно для того, чтобы
      // потребитель напечатал как есть, и десктоп это чтит (chart-read.js —
      // `if (it.tab) …`). Через lineToWords аппликатура рассыпалась на слова,
      // которые вправе перенестись по пробелам, а скобки {sog}-сетки съедались
      // как аккорды и вместе с тональностью ЕХАЛИ: `| [C] |` при сдвиге +2
      // печаталось как `D`. Транспонировать сетку нельзя — в ней не аккорды
      // песни, а её ритмическая запись, и трогать её мы права не имеем.
      if (l.tab) { box.appendChild(el('div', 'cw-tab', l.txt)); continue; }
      // Пустая строка ВНУТРИ блока — авторская отбивка, а не мусор: ядро
      // подрезает пустые строки только по краям блока и оставляет внутренние
      // намеренно («внутри — дыхание автора», doc.js). Через lineToWords такая
      // строка даёт пустой список слов, то есть пустой флекс-ряд нулевой высоты,
      // и отбивка молча пропадала. Десктоп печатает её `.cp-gap`.
      if (!l.txt.trim()) { box.appendChild(el('div', 'cw-gap')); continue; }
      box.appendChild(renderLine(l.txt, shift));
    }
    sheet.appendChild(box);
  });
}
